import { z } from 'zod';
import { zStringRequired } from '../../z';
export const ptVisitValidationSchema = z.object({
    id: z.string(),
    data: z.object({
        PlanOfCare: z.boolean().refine((value) => !!value, { message: 'section has errors' }),
        subjective: z.object({
            timeIn: z.string().optional(),
            timeOut: z.string().optional(),
            patientReporting: z.string().optional(),
            functionalStability: z.string().optional(),
            stabilityBarriers: z.string().optional(),
            plansForVisit: z.string().optional(),
            plansFromLastVisit: z.string().optional(),
            bp: z.object({
                rest: z.object({
                    diastolic: z.string().optional(),
                    systolic: z.string().optional(),
                }),
                activity: z.object({
                    diastolic: z.string().optional(),
                    systolic: z.string().optional(),
                }),
            }),
            sp: z.object({
                rest: z.string().optional(),
                activity: z.string().optional(),
            }),
            heartRate: z.object({
                rest: z.string().optional(),
                activity: z.string().optional(),
            }),
            respiration: z.object({
                rest: z.string().optional(),
                activity: z.string().optional(),
            }),
        }),
        mentalStatus: z.object({
            alertness: z.string().optional(),
            orientedToPerson: z.string().optional(),
            orientedToPlace: z.string().optional(),
            orientedToPurpose: z.string().optional(),
            orientedToTime: z.string().optional(),
            status: z.array(z.string()).optional(),
            aggravatingFactor: z.string().optional(),
        }),
        followCommands: z.array(z.string()).optional(),
        assistanceFrom: z.object({
            current: z.string().optional(),
            prior: z.string().optional(),
        }),
        impairments: z.object({
            affecting: z.array(z.string()).optional(),
            affectingSpecify: z.string().optional(),
            homeboundDueTo: z.string().optional(),
            visitSummary: z.string().optional(),
        }),
        ptcgEducation: z.object({
            education: z.array(z.string()).optional(),
            dischargePlanning: z.string().optional(),
            learningStatus: z.string().optional(),
            other: z.string().optional(),
        }),
        selfCare: z.object({
            grooming: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            toileting: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            bathing: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            dressingUE: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            dressingLE: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            socks: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
        }),
        transfers: z.object({
            bed: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            sitStand: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            toilet: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            chair: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            floor: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            shower: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            car: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
        }),
        mobility: z.object({
            indoor: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            outdoor: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            stepCurb: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
        }),
        functional: z.object({
            mealPrep: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
            houseChores: z
                .object({
                goal: z.string().optional(),
                plof: z.string().optional(),
                clof: z.string().optional(),
            })
                .optional(),
        }),
        functionalActivites: z.object({
            openDoor: z.string().optional(),
            stepUpDown: z.string().optional(),
            liftObjects: z.string().optional(),
            carryObjects: z.string().optional(),
            stairs: z.string().optional(),
            car: z.string().optional(),
        }),
        motivation: z.object({
            motivation: z.array(z.string()).optional(),
            motivationOther: z.string().optional(),
        }),
        overallProgress: z.object({
            progress: z.array(z.string()).optional(),
            progressOther: z.string().optional(),
        }),
        overallAssessment: z.string().optional(),
        session: z.object({
            tolerance: z.array(z.string()).optional(),
            toleranceOther: z.string().optional(),
        }),
        reasonContinuePT: z.object({
            reason: z.array(z.string()).optional(),
            reasonOther: z.string().optional(),
        }),
        nextVisitPlans: z.object({
            plans: z.array(z.string()).optional(),
            plansOther: z.string().optional(),
        }),
        coordination: z.object({
            nameTitle: z.string().optional(),
            reviewed: z.string().optional(),
            planned: z.string().optional(),
        }),
        misc: z.object({
            nextAppointment: z.string().optional(),
            notes: z.array(z.string()).optional(),
            notesOther: z.string().optional(),
        }),
    }),
    signedUserId: zStringRequired(),
    signedUserDate: zStringRequired(),
});
